import './styles.css';

import React, { useState, createContext, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import routes from './routes';
import { ThemeProvider } from '@mui/material';
import b2bTheme from './components/b2b-common/theme';
import GlobalStyles from './styles/GlobalStyles';
import { fetchMyOrganizationsData } from './services/b2b-users-apiService';
import { fetchFrameworksData } from './services/b2b-frameworks-apiService';
import { fetchAPIDataWhileSettingState } from './components/b2b-common/helpers';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useB2BRoute } from './components/b2b-common/hoc/B2BRoute';
import PublicPostPage from './components/b2b-social/pages/PublicPostPage';
import Notification from './components/b2b-common/Notification';
import SignUpNewMemberPage from './components/b2b-users/pages/SignUpNewMemberPage';
import PublicDashboardDetailsPage from './components/b2b-reporting/pages/PublicDashboardDetailsPage';
import useFetchingBasicUserData from './components/b2b-users/hooks/useFetchingBasicUserData';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const LoginContext = createContext();
const GenFrameworksContext = createContext();
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Enable query retries
            retry: 1,
            // Enable cache time
            cacheTime: 1000 * 60 * 5, // 5 minutes
            // Enable stale time
            staleTime: 1000 * 60 * 1, // 1 minute
        },
    },
    logger: {
        log: console.log,
        warn: console.warn,
        error: console.error,
    },
});

function App() {
    const localStorageData = localStorage.getItem('loginData');
    const [loginData, setLoginData] = useState(localStorageData ? JSON.parse(localStorageData) : null);
    const { userData } = useFetchingBasicUserData( loginData?.userUuid, loginData?.accessToken);
    const [myOrganizations, setMyOrganizations] = React.useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState('users');
    const [marIaSideBarOpen, setMarIaSideBarOpen] = useState(false);
    const [notification, setNotification] = useState(null);

    const [genericFrameworks, setGenericFrameworks] = useState(null);

    const publicRoutes = [
        { path: '/', element: <Navigate to="/login" />, visibility: 'public' },
        { path: '/login', element: <LoginPage onLogin={(loginData) => handleLogin(loginData)} />, visibility: 'public' },
        { path: '/pub/social/post/:postId', element: <PublicPostPage />, visibility: 'public' },
        { path: '/pub/reporting/dashboard/:dashboardId', element: <PublicDashboardDetailsPage />, visibility: 'public' },
        { path: '/signup/', element: <SignUpNewMemberPage />, visibility: 'public' },
    ];

    useEffect(() => {
        const storedLoginData = localStorage.getItem('loginData');
        if (storedLoginData) {
            const parsedLoginData = JSON.parse(storedLoginData);
            setLoginData(parsedLoginData);
        }
    }, []);
    
    useEffect(() => {
        if (loginData) {
            localStorage.setItem('loginData', JSON.stringify(loginData));
            // Fetch user data and organizations
            handleFetchingMyOrganizations();
            fetchAllGenericFrameworks();
        }
    }, [loginData]);

    const fetchAllGenericFrameworks = async () => {
        try {
            const paramsArray = [
                'generic',
                loginData.accessToken,
                true, // fetch images
                true, // include lelvel1s
            ];
            const fws = await fetchAPIDataWhileSettingState(fetchFrameworksData, paramsArray, null);
            setGenericFrameworks(fws);
        } catch (error) {
            console.error('Failed to fetch frameworks:', error.message);
        }
    };

    const handleFetchingMyOrganizations = async () => {
        try {
            if (loginData) {
                const { accessToken } = loginData;
                const fetchedOrganizations = await fetchMyOrganizationsData(accessToken);
                const mappedOrganizations = fetchedOrganizations.map(item => ({
                    uuid: item.organization.organizationUUID,
                    organizationName: item.organization.organizationName,
                    image: item.organization.imagePath,
                }));
                //console.log(mappedOrganizations);
                //console.log(loginData);
                setMyOrganizations(mappedOrganizations);
            }
        } catch (error) {
            console.error('Failed to fetch my organization:', error.message);
        }
    };

    const navigate = useNavigate(); // Use useNavigate directly

    const handleLogin = (loginData) => {
        setLoginData(loginData);
        setSelectedItem('home');
        navigate('/reporting/a2030-briefing');
    };

    return (
        <ThemeProvider theme={b2bTheme}>
            <QueryClientProvider client={queryClient}>
                <LoginContext.Provider
                    value={{
                        loginData, setLoginData,
                        userData,
                        myOrganizations,
                        sidebarOpen, setSidebarOpen,
                        selectedItem, setSelectedItem,
                        marIaSideBarOpen, setMarIaSideBarOpen,
                        setNotification,
                    }}>
                    <GenFrameworksContext.Provider value={{ genericFrameworks }}>
                        <GlobalStyles />
                        <Routes>
                            {useB2BRoute(publicRoutes)}
                            {routes.map(route => (
                                <Route key={route.path} path={route.path} element={route.element} />
                            ))}
                        </Routes>
                    </GenFrameworksContext.Provider>
                </LoginContext.Provider>
                {/* <ReactQueryDevtools initialIsOpen={false} />  */}
            </QueryClientProvider>
            <Notification notification={notification} />
        </ThemeProvider>
    );
}

export default App;
export { LoginContext, GenFrameworksContext };
export const useLoginContext = () => React.useContext(LoginContext);